import { useEffect, useRef, useState } from 'react';
import type { ShiftPlannedReportPage, ShiftPlannedReportRow, ShiftPlannedReportEmployee, ShiftPlannedReportSource, ShiftPlannedReportView, ShiftComparisonReportPage, ShiftComparisonReportRow } from 'shared-models';
import type { ShiftTipReportPage,ShiftTipReportRow,ShiftTipReportEmployee,ShiftTipReportDaily,ShiftTipReportView } from 'shared-models';
import { shiftReportsService } from '../../services/reports/shift-reports.service';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { dateKey,monthStart,minutesLabel,comparisonReportValues,tipReportValues,appendReportRows } from './manager-reports-page.utils';

export function usePlannedReportsController(kind:boolean | 'tips' = false) {
  const comparison=kind===true;
  const tips=kind==='tips';
  const [from,setFrom] = useState(monthStart);
  const [to,setTo] = useState(() => dateKey(new Date()));
  const [source,setSource] = useState<ShiftPlannedReportSource>(comparison ? 'published' : 'draft');
  const [view,setViewValue] = useState<ShiftTipReportView>('detail');
  const [employeePk,setEmployeePk] = useState<number | null>(null);
  const [report,setReport] = useState<ShiftPlannedReportPage | ShiftComparisonReportPage | ShiftTipReportPage | null>(null);
  const [exceptionsOnly,setExceptionsOnly] = useState(false);
  const [isLoading,setIsLoading] = useState(true);
  const [isLoadingMore,setIsLoadingMore] = useState(false);
  const [isExporting,setIsExporting] = useState(false);
  const [error,setError] = useState<string | null>(null);
  const [isOutdated,setIsOutdated] = useState(false);
  const [recovery,setRecovery] = useState(0);
  const generation = useRef(0);
  const pageLock = useRef(false);
  const exportLock = useRef(false);
  const outdated = useRef(false);
  const ready = useShiftEvents(event => {
    if (event.type==='sse.resync') {setRecovery(current => current+1);return;}
    if (tips) {
      const changedDate=event.type==='tip.upserted' ? event.tip.date : event.type==='tip.sourceChanged' ? event.overview.source.date
        : event.type==='attendance.upserted' ? event.attendance?.date : null;
      if (event.type==='tip.removed' || event.type==='attendance.removed' || ((event.type==='tip.upserted' || event.type==='tip.sourceChanged' || event.type==='attendance.upserted') && (!changedDate || (changedDate>=from && changedDate<=to)))) {
        outdated.current=true;setIsOutdated(true);
      }
      return;
    }
    if (comparison && (event.type==='attendance.upserted' || event.type==='attendance.removed')) {outdated.current=true;setIsOutdated(true);return;}
    const week = source==='draft' && event.type==='schedule.draftChanged' ? event.snapshot.weekStartDate
      : source==='published' && event.type==='schedule.publicationChanged' ? event.weekStartDate : null;
    if (!week) return;
    const end = new Date(`${week}T12:00:00`);end.setDate(end.getDate()+6);
    const lower = new Date(`${from}T12:00:00`);lower.setDate(lower.getDate()-(comparison ? 1 : 0));
    const upper = new Date(`${to}T12:00:00`);upper.setDate(upper.getDate()+(comparison ? 1 : 0));
    if (week<=dateKey(upper) && dateKey(end)>=dateKey(lower)) {outdated.current=true;setIsOutdated(true);}
  });
  const query = {from,to,...(tips ? {view} : comparison ? {exceptionsOnly} : {source,view}),...(employeePk!==null ? {employeePk} : {})};
  const setView=(next:ShiftTipReportView) => {
    if (exportLock.current || (next==='daily' && !tips)) return;
    if (next==='daily') setEmployeePk(null);
    setViewValue(next);
  };
  const getReport=(page:{offset?:number;limit?:number}={}) => tips ? shiftReportsService.getTips({...query,...page,view})
    : comparison ? shiftReportsService.getComparison({...query,...page})
    : shiftReportsService.getPlanned({...query,...page,view:view==='daily' ? 'detail' : view});
  useEffect(() => {
    if (!ready) return;
    let active = true;
    const requested = ++generation.current;
    outdated.current=false;setReport(null);setIsLoading(true);setError(null);setIsOutdated(false);
    void Promise.resolve().then(async () => {
      if (!active) return;
      try {
        const snapshot = await getReport();
        if (active && requested===generation.current) setReport(snapshot);
      } catch (failure) {if (active) setError(failure instanceof Error && failure.message.includes('COMPARISON_TOO_LARGE')
        ? 'יש לצמצם את טווח הדוח.' : 'לא ניתן לטעון את הדוח.');}
      finally {if (active) setIsLoading(false);}
    });
    return () => {active=false;if (requested===generation.current) generation.current+=1;};
  },[kind,from,to,source,view,employeePk,exceptionsOnly,ready,recovery]);

  const loadMore = async () => {
    if (pageLock.current || exportLock.current || !report?.pagination.hasMore || isLoading || outdated.current) return;
    pageLock.current=true;setIsLoadingMore(true);setError(null);
    const requested = generation.current;
    try {
      const next = await getReport({...query,offset:report.rows.length,limit:50});
      if (requested!==generation.current) return;
      if (outdated.current) return;
      if (next.fingerprint!==report.fingerprint || ('view' in next && (!('view' in report) || next.view!==report.view)) || next.source!==report.source) {
        outdated.current=true;setIsOutdated(true);setError('הדוח השתנה. יש לעדכן את הדוח.');return;
      }
      setReport(current => {
        if (!current) return current;
        if (current.source==='calculated' || next.source==='calculated') {
          if (current.source==='calculated' && next.source==='calculated' && current.view==='daily' && next.view==='daily') return {...next,rows:appendReportRows(current.rows,next.rows)};
          if (current.source==='calculated' && next.source==='calculated' && current.view==='detail' && next.view==='detail') return {...next,rows:[...current.rows,...next.rows.filter(row => !current.rows.some(existing => existing.id===row.id))]};
          if (current.source==='calculated' && next.source==='calculated' && current.view==='summary' && next.view==='summary') return {...next,rows:[...current.rows,...next.rows.filter(row => !current.rows.some(existing => existing.employeePk===row.employeePk))]};
          return current;
        }
        if (!('view' in current) && !('view' in next)) return {...next,rows:[...current.rows,...next.rows.filter(row => !current.rows.some(existing => existing.id===row.id))]};
        if ('view' in current && 'view' in next && current.view==='detail' && next.view==='detail') return {...next,rows:[...current.rows,...next.rows.filter(row => !current.rows.some(existing => existing.id===row.id))]};
        if ('view' in current && 'view' in next && current.view==='summary' && next.view==='summary') return {...next,rows:[...current.rows,...next.rows.filter(row => !current.rows.some(existing => existing.employeePk===row.employeePk))]};
        return current;
      });
    } catch {if (requested===generation.current) setError('לא ניתן לטעון רישומים נוספים.');}
    finally {pageLock.current=false;setIsLoadingMore(false);}
  };

  const exportExcel = async () => {
    if (exportLock.current || pageLock.current || !report?.rows.length || isLoading || outdated.current) return;
    exportLock.current=true;setIsExporting(true);setError(null);
    const requested = generation.current;
    try {
      const rows:Array<ShiftPlannedReportRow | ShiftPlannedReportEmployee | ShiftComparisonReportRow | ShiftTipReportRow | ShiftTipReportEmployee | ShiftTipReportDaily> = [];
      let hasMore = true;
      while (hasMore) {
        const next = await getReport({...query,offset:rows.length,limit:100});
        if (requested!==generation.current) return;
        if (outdated.current) throw new Error('REPORT_CHANGED');
        if (next.fingerprint!==report.fingerprint || ('view' in next && next.view!==view) || next.source!==report.source) {
          outdated.current=true;setIsOutdated(true);throw new Error('REPORT_CHANGED');
        }
        rows.push(...next.rows);hasMore=next.pagination.hasMore;
        if (rows.length>10000 || (rows.length===10000 && hasMore)) throw new Error('REPORT_TOO_LARGE');
        if (hasMore && !next.rows.length) throw new Error('Empty report page');
      }
      const XLSX = await import('xlsx');
      if (requested!==generation.current) return;
      if (outdated.current) throw new Error('REPORT_CHANGED');
      const values = rows.map(row => 'finalTip' in row ? tipReportValues(row) : 'planned' in row ? comparisonReportValues(row) : 'date' in row ? {
        עובד:row.employeeName,תאריך:row.date,מחלקה:row.sectionName,משמרת:row.shiftName,תפקיד:row.roleName,
        התחלה:row.startHour,סיום:row.endHour,'שעות מתוכננות':minutesLabel(row.scheduledMinutes),
      } : {עובד:row.employeeName,שיבוצים:row.assignments,'שעות מתוכננות':minutesLabel(row.scheduledMinutes)});
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book,XLSX.utils.json_to_sheet(values),tips ? 'Tips' : comparison ? 'Comparison' : 'Planned');
      XLSX.writeFile(book,tips ? `shift-tips-${view}-${from}-${to}.xlsx` : comparison ? `shift-comparison-published-${exceptionsOnly ? 'exceptions' : 'all'}-${from}-${to}.xlsx` : `shift-planned-${source}-${view}-${from}-${to}.xlsx`);
    } catch (failure) {
      if (requested===generation.current) setError(failure instanceof Error && failure.message==='REPORT_CHANGED'
        ? 'הדוח השתנה. יש לעדכן לפני ייצוא.' : failure instanceof Error && failure.message==='REPORT_TOO_LARGE'
          ? 'הייצוא מוגבל ל-10000 שורות. יש לצמצם את הטווח.' : 'לא ניתן לייצא את הדוח.');
    } finally {exportLock.current=false;setIsExporting(false);}
  };
  return {from,setFrom,to,setTo,source,setSource,view,setView,employeePk,setEmployeePk,exceptionsOnly,setExceptionsOnly,report,isLoading,isLoadingMore,isExporting,error,isOutdated,
    loadMore,exportExcel,refresh:() => {if (!exportLock.current && !pageLock.current && !isLoading) setRecovery(current => current+1);},
    drillDown:(id:number) => {if (!exportLock.current) {setEmployeePk(id);setView('detail');}},
    clearEmployee:() => {if (!exportLock.current) setEmployeePk(null);},
  };
}